import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import * as Icons from "lucide-react";

export const B2BDataSecurity = () => {
  const [refSecurity, isSecurityVisible] = useScrollReveal({ once: true });
  const [refStandards, isStandardsVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section - Apple Style */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Datenlöschung & Sicherheit
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            DSGVO-konforme Datenlöschung mit Zertifikat
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/request-quote">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt anfragen
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Security Features */}
      <section ref={refSecurity} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate={isSecurityVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-[#143e6e] mb-8">
              DSGVO-konforme Datenlöschung
            </motion.h2>
            <motion.p variants={slideUp} className="text-lg text-gray-700 mb-12">
              Bei der Veräußerung gebrauchter IT-Hardware ist die sichere und nachweisbare Löschung aller Daten gesetzlich vorgeschrieben. Wir garantieren eine DSGVO-konforme Datenlöschung nach höchsten Standards.
            </motion.p>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {[
                { icon: Icons.CheckCircle, title: "Software-Löschung", desc: "Mehrfaches Überschreiben nach BSI-Standard" },
                { icon: Icons.FileCheck, title: "Löschzertifikat", desc: "Dokumentierte Bestätigung für jedes Gerät" },
                { icon: Icons.Trash2, title: "Physische Vernichtung", desc: "Optional: Zerstörung von Datenträgern" },
                { icon: Icons.Shield, title: "Audit-Sicherheit", desc: "Vollständige Dokumentation für Compliance" }
              ].map((item, idx) => (
                <motion.div key={idx} variants={slideUp}>
                  <PremiumCard className="p-6">
                    <div className="flex items-start gap-4">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        className="flex-shrink-0"
                      >
                        <item.icon className="text-green-600" size={32} />
                      </motion.div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-[#143e6e]">{item.title}</h3>
                        <p className="text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  </PremiumCard>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Standards */}
      <section ref={refStandards} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isStandardsVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Unsere Sicherheitsstandards
          </motion.h2>

          <motion.div
            initial="hidden"
            animate={isStandardsVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {[
              { icon: Icons.FileCheck, title: "BSI-Richtlinien", desc: "Löschung nach BSI TR-03159" },
              { icon: Icons.Lock, title: "ISO 27001", desc: "Zertifizierte Prozesse" },
              { icon: Icons.Award, title: "DSGVO-Konform", desc: "100% rechtskonform" }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 text-center h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center"
                  >
                    <item.icon className="text-white" size={32} />
                  </motion.div>
                  <h3 className="font-bold text-xl mb-3 text-[#143e6e]">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Sichere Datenlöschung gewünscht?
          </motion.h2>
          <motion.p variants={slideUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Fordern Sie jetzt Ihr Angebot mit DSGVO-konformer Löschung an
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/request-quote">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Angebot anfordern
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};
